import React, { useMemo, useState } from "react";
import Text from "@/components/ui/text";
import SingleSelect from "@/components/controls/SingleSelect";
import UserAvatar from "@/components/controls/UserAvatar";
import { UserData, UserStatus } from "@/entities/users/types";
import { useUsersInfinite } from "@/entities/users/hooks";

const ACTIVE_USER_FILTERS = { status: [UserStatus.ACTIVE] };

interface RecordTransferRowProps {
  type: "companies" | "deals" | "leads";
  label: string;
  count: number;
  selectedValue: string | null;
  onTransferChange: (
    type: "companies" | "deals" | "leads",
    value: string
  ) => void;
  t: (key: string) => string;
  translationKey: string;
}

const RecordTransferRow: React.FC<RecordTransferRowProps> = ({
  type,
  label,
  count,
  selectedValue,
  onTransferChange,
  t,
  translationKey
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const search = useMemo(
    () => (searchTerm ? { term: searchTerm } : undefined),
    [searchTerm]
  );

  const { entities, isLoading } = useUsersInfinite(
    ACTIVE_USER_FILTERS,
    search
  );

  const availableUsers = useMemo(
    () =>
      entities.map((user) => ({
        id: user.id,
        displayName: user.displayName,
        subtitle: user.jobTitle || user.departmentName || "",
        userData: user
      })),
    [entities]
  );

  const renderUserIcon = (userId: string | number): JSX.Element => {
    const user = entities.find((u) => u.id === userId) as UserData | undefined;
    if (!user) {
      return <div className="size-6" />;
    }
    return (
      <UserAvatar
        userData={user}
        size="M"
        fallbackClassName="bg-gradient-to-t from-slate-600 to-tertiary text-xs"
      />
    );
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchTerm("");
    }
  };

  if (count === 0) return null;

  return (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <Text variant="body" className="min-w-0">
          {label}
        </Text>
        <div className="flex items-center justify-center min-w-[24px] h-6 px-2 rounded-sm bg-surface-elevation2 border border-border-border2">
          <Text variant="smBody" className="font-medium">
            {count}
          </Text>
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Text variant="bodySecondary" className="whitespace-nowrap">
          {t(`${translationKey}.handOverTo`)}
        </Text>
        <div className="w-[314px]">
          <SingleSelect
            fieldName={`transfer-${type}`}
            options={availableUsers}
            selectedValue={selectedValue || ""}
            onSelection={(value) => onTransferChange(type, value as string)}
            placeholder={t(`${translationKey}.select`)}
            variant="form"
            showSearch={true}
            isOpen={isOpen}
            onOpenChange={handleOpenChange}
            onSearch={setSearchTerm}
            isLoading={isLoading}
            className="w-full"
            renderIcon={renderUserIcon}
            popoverClassName="w-[314px]"
          />
        </div>
      </div>
    </div>
  );
};

export default RecordTransferRow;
